import Phaser from 'phaser';

/**
 * BootScene: Handles asset preloading with a progress bar.
 * Transitions to TitleScene when all assets are loaded.
 */
export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' });
  }

  preload(): void {
    // Create loading bar
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    const progressBar = this.add.graphics();
    const progressBox = this.add.graphics();
    progressBox.fillStyle(0x222222, 0.8);
    progressBox.fillRect(width / 2 - 160, height / 2 - 25, 320, 50);

    const loadingText = this.add.text(width / 2, height / 2 - 50, 'Loading...', {
      fontSize: '20px',
      color: '#00ffff',
      fontFamily: '"Press Start 2P", monospace',
    });
    loadingText.setOrigin(0.5, 0.5);

    const percentText = this.add.text(width / 2, height / 2, '0%', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: '"Press Start 2P", monospace',
    });
    percentText.setOrigin(0.5, 0.5);

    // Progress events
    this.load.on('progress', (value: number) => {
      percentText.setText(`${Math.round(value * 100)}%`);
      progressBar.clear();
      progressBar.fillStyle(0x00ffff, 1);
      progressBar.fillRect(width / 2 - 150, height / 2 - 15, 300 * value, 30);
    });

    this.load.on('complete', () => {
      progressBar.destroy();
      progressBox.destroy();
      loadingText.destroy();
      percentText.destroy();
    });

    // Handle asset load errors gracefully (audio files may fail in restricted environments)
    this.load.on('loaderror', (file: Phaser.Loader.File) => {
      console.warn(`Failed to load asset: ${file.key} (${file.url}) - game will continue without it`);
    });

    // Load game assets
    this.loadAssets();
  }

  private loadAssets(): void {
    // Load audio assets
    this.load.audio('shoot', 'assets/audio/shoot.wav');
    this.load.audio('explosion', 'assets/audio/explosion.wav');
    this.load.audio('alien-explosion', 'assets/audio/alien-explosion.wav');
    this.load.audio('march1', 'assets/audio/march1.wav');
    this.load.audio('march2', 'assets/audio/march2.wav');
    this.load.audio('ufo', 'assets/audio/ufo.wav');
    this.load.audio('powerup', 'assets/audio/powerup.wav');
  }

  create(): void {
    // Generate placeholder graphics for game entities
    this.generatePlaceholderTextures();

    // Transition to TitleScene
    this.scene.start('TitleScene');
  }

  private generatePlaceholderTextures(): void {
    // Player ship (cyan triangle with neon glow)
    const playerGfx = this.add.graphics();
    // Outer glow layer (dim cyan)
    playerGfx.fillStyle(0x007777, 0.25);
    playerGfx.fillTriangle(20, -4, -4, 40, 44, 40);
    // Mid glow layer
    playerGfx.fillStyle(0x00aaaa, 0.35);
    playerGfx.fillTriangle(19, -1, 0, 38, 40, 38);
    // Main ship body
    playerGfx.fillStyle(0x00ffff, 1);
    playerGfx.fillTriangle(20, 2, 4, 36, 36, 36);
    // Inner highlight
    playerGfx.fillStyle(0xaaffff, 0.6);
    playerGfx.fillTriangle(20, 8, 12, 30, 28, 30);
    playerGfx.generateTexture('player', 40, 40);
    playerGfx.destroy();

    // Alien types
    this.generateAlienTexture('alien-top', 0xff00ff);
    this.generateAlienTexture('alien-middle', 0x00ff41);
    this.generateAlienTexture('alien-bottom', 0xffff00);

    // Player projectiles (white with neon glow)
    const bulletGfx = this.add.graphics();
    // Outer glow halo
    bulletGfx.fillStyle(0x4488ff, 0.2);
    bulletGfx.fillRect(0, 0, 12, 20);
    // Mid glow
    bulletGfx.fillStyle(0x88ccff, 0.4);
    bulletGfx.fillRect(2, 1, 8, 18);
    // Core bright white
    bulletGfx.fillStyle(0xffffff, 1);
    bulletGfx.fillRect(4, 2, 4, 16);
    // Inner bright hot center
    bulletGfx.fillStyle(0xffffff, 1);
    bulletGfx.fillRect(5, 3, 2, 14);
    bulletGfx.generateTexture('bullet-player', 12, 20);
    bulletGfx.destroy();

    // Player bullet glow sprite (larger, for additive blend overlay)
    const bulletGlowGfx = this.add.graphics();
    bulletGlowGfx.fillStyle(0x00ccff, 0.5);
    bulletGlowGfx.fillEllipse(10, 12, 16, 28);
    bulletGlowGfx.fillStyle(0x88ddff, 0.3);
    bulletGlowGfx.fillEllipse(10, 12, 20, 32);
    bulletGlowGfx.generateTexture('bullet-player-glow', 20, 24);
    bulletGlowGfx.destroy();

    // Alien projectiles (red/orange zigzag - visually distinct from player's white beam)
    const alienBulletGfx = this.add.graphics();
    // Outer glow halo
    alienBulletGfx.fillStyle(0xff2222, 0.2);
    alienBulletGfx.fillRect(0, 0, 12, 20);
    // Mid glow
    alienBulletGfx.fillStyle(0xff4433, 0.35);
    alienBulletGfx.fillRect(1, 0, 10, 20);
    // Zigzag pattern for visual distinction
    alienBulletGfx.fillStyle(0xff4444, 1);
    alienBulletGfx.fillRect(6, 1, 3, 4);
    alienBulletGfx.fillRect(3, 5, 3, 4);
    alienBulletGfx.fillRect(6, 9, 3, 4);
    alienBulletGfx.fillRect(3, 13, 3, 4);
    // Bright center
    alienBulletGfx.fillStyle(0xff8866, 1);
    alienBulletGfx.fillRect(7, 2, 1, 3);
    alienBulletGfx.fillRect(4, 6, 1, 3);
    alienBulletGfx.fillRect(7, 10, 1, 3);
    alienBulletGfx.fillRect(4, 14, 1, 3);
    alienBulletGfx.generateTexture('bullet-alien', 12, 20);
    alienBulletGfx.destroy();

    // Alien bullet glow sprite
    const alienBulletGlowGfx = this.add.graphics();
    alienBulletGlowGfx.fillStyle(0xff3333, 0.5);
    alienBulletGlowGfx.fillEllipse(10, 12, 14, 26);
    alienBulletGlowGfx.fillStyle(0xff5544, 0.3);
    alienBulletGlowGfx.fillEllipse(10, 12, 18, 30);
    alienBulletGlowGfx.generateTexture('bullet-alien-glow', 20, 24);
    alienBulletGlowGfx.destroy();

    // UFO
    const ufoGfx = this.add.graphics();
    ufoGfx.fillStyle(0xff3333, 1);
    ufoGfx.fillEllipse(20, 12, 40, 20);
    ufoGfx.fillStyle(0xff6666, 1);
    ufoGfx.fillEllipse(20, 8, 20, 10);
    ufoGfx.generateTexture('ufo', 40, 24);
    ufoGfx.destroy();

    // Shield block (blue-green #00cc88 with subtle neon glow)
    const shieldGfx = this.add.graphics();
    // Outer glow layer
    shieldGfx.fillStyle(0x009966, 0.4);
    shieldGfx.fillRect(0, 0, 6, 6);
    // Main block body
    shieldGfx.fillStyle(0x00cc88, 1);
    shieldGfx.fillRect(1, 1, 4, 4);
    // Inner highlight for neon pop
    shieldGfx.fillStyle(0x00ffaa, 0.6);
    shieldGfx.fillRect(2, 2, 2, 2);
    shieldGfx.generateTexture('shield-block', 6, 6);
    shieldGfx.destroy();

    // Life icon (small player ship with glow)
    const lifeGfx = this.add.graphics();
    lifeGfx.fillStyle(0x00aaaa, 0.3);
    lifeGfx.fillTriangle(8, -1, -1, 17, 17, 17);
    lifeGfx.fillStyle(0x00ffff, 1);
    lifeGfx.fillTriangle(8, 0, 0, 16, 16, 16);
    lifeGfx.generateTexture('life-icon', 16, 16);
    lifeGfx.destroy();

    // Particle (small circle)
    const particleGfx = this.add.graphics();
    particleGfx.fillStyle(0xffffff, 1);
    particleGfx.fillCircle(4, 4, 4);
    particleGfx.generateTexture('particle', 8, 8);
    particleGfx.destroy();

    // Star for background
    const starGfx = this.add.graphics();
    starGfx.fillStyle(0xffffff, 1);
    starGfx.fillCircle(1, 1, 1);
    starGfx.generateTexture('star', 2, 2);
    starGfx.destroy();

    // Larger star for far background layer (dimmer)
    const starDimGfx = this.add.graphics();
    starDimGfx.fillStyle(0x888899, 1);
    starDimGfx.fillCircle(1, 1, 1);
    starDimGfx.generateTexture('star-dim', 2, 2);
    starDimGfx.destroy();

    // Thruster/engine glow texture for player ship
    const thrusterGfx = this.add.graphics();
    // Outer glow (wide, dim cyan exhaust)
    thrusterGfx.fillStyle(0x0044ff, 0.2);
    thrusterGfx.fillEllipse(16, 14, 28, 30);
    // Mid glow
    thrusterGfx.fillStyle(0x0088ff, 0.4);
    thrusterGfx.fillEllipse(16, 12, 18, 24);
    // Inner bright core
    thrusterGfx.fillStyle(0x00ccff, 0.7);
    thrusterGfx.fillEllipse(16, 10, 10, 16);
    // Hot center
    thrusterGfx.fillStyle(0xccffff, 0.9);
    thrusterGfx.fillEllipse(16, 8, 5, 10);
    thrusterGfx.generateTexture('thruster-glow', 32, 32);
    thrusterGfx.destroy();
  }

  private generateAlienTexture(key: string, color: number): void {
    const size = 32; // Slightly larger canvas for glow
    const gfx = this.add.graphics();

    // Draw glow layer (slightly larger, lower alpha) for neon effect
    const glowColor = color;
    const r = (glowColor >> 16) & 0xff;
    const g = (glowColor >> 8) & 0xff;
    const b = glowColor & 0xff;
    const dimColor = ((r >> 1) << 16) | ((g >> 1) << 8) | (b >> 1);

    if (key === 'alien-top') {
      // Type 1 (Top row - Magenta): Squid-like / tentacle shape
      // Glow outline
      gfx.fillStyle(dimColor, 0.35);
      gfx.fillCircle(16, 8, 12);
      // Main body - rounded top with tentacles
      gfx.fillStyle(color, 1);
      gfx.fillRect(10, 2, 12, 4);   // top
      gfx.fillRect(6, 6, 20, 4);    // upper body
      gfx.fillRect(4, 10, 24, 4);   // mid body
      gfx.fillRect(6, 14, 20, 4);   // lower body
      // Tentacles
      gfx.fillRect(4, 18, 4, 6);    // left tentacle
      gfx.fillRect(12, 18, 4, 4);   // center-left tentacle
      gfx.fillRect(18, 18, 4, 4);   // center-right tentacle
      gfx.fillRect(24, 18, 4, 6);   // right tentacle
      // Eyes
      gfx.fillStyle(0x000000, 1);
      gfx.fillRect(10, 10, 3, 3);
      gfx.fillRect(19, 10, 3, 3);
      gfx.generateTexture(key, size, 26);
    } else if (key === 'alien-middle') {
      // Type 2 (Middle rows - Green): Crab-like with claws
      // Glow outline
      gfx.fillStyle(dimColor, 0.35);
      gfx.fillCircle(16, 12, 14);
      // Main body
      gfx.fillStyle(color, 1);
      gfx.fillRect(8, 2, 16, 4);    // top head
      gfx.fillRect(4, 6, 24, 4);    // upper body
      gfx.fillRect(2, 10, 28, 4);   // widest body
      gfx.fillRect(4, 14, 24, 4);   // lower body
      gfx.fillRect(8, 18, 16, 4);   // bottom
      // Claws/antennae
      gfx.fillRect(2, 2, 4, 6);     // left antenna
      gfx.fillRect(26, 2, 4, 6);    // right antenna
      gfx.fillRect(0, 18, 6, 4);    // left claw
      gfx.fillRect(26, 18, 6, 4);   // right claw
      // Eyes
      gfx.fillStyle(0x000000, 1);
      gfx.fillRect(9, 8, 4, 4);
      gfx.fillRect(19, 8, 4, 4);
      gfx.generateTexture(key, size, 24);
    } else {
      // Type 3 (Bottom rows - Yellow): Classic round/octopus shape
      // Glow outline
      gfx.fillStyle(dimColor, 0.35);
      gfx.fillCircle(16, 10, 13);
      // Main body - rounder shape
      gfx.fillStyle(color, 1);
      gfx.fillRect(10, 0, 12, 4);   // top
      gfx.fillRect(6, 4, 20, 4);    // upper
      gfx.fillRect(4, 8, 24, 4);    // wide
      gfx.fillRect(4, 12, 24, 4);   // wide
      gfx.fillRect(6, 16, 20, 4);   // lower
      // Wavy bottom legs
      gfx.fillRect(6, 20, 4, 4);
      gfx.fillRect(14, 20, 4, 4);
      gfx.fillRect(22, 20, 4, 4);
      // Eyes
      gfx.fillStyle(0x000000, 1);
      gfx.fillRect(9, 8, 4, 4);
      gfx.fillRect(19, 8, 4, 4);
      gfx.generateTexture(key, size, 26);
    }

    gfx.destroy();
  }
}
